"use client";

import { IconComponent } from "@/types/common";
import { Card, CardBody } from "@heroui/react";
import clsx from "clsx";
import { useState } from "react";

interface StatCardSide {
  color: keyof typeof colors;
  description: string;
  icon: IconComponent;
  value: number;
  title: string;
}

interface StatCardProps extends StatCardSide {
  backSide?: Partial<StatCardSide>;
}

export function StatCard({ color, description, icon: Icon, value, title, backSide }: StatCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const frontSide: StatCardSide = { color, description, icon: Icon, value, title };
  const hasBackSide = !!backSide;

  const resolvedBackSide: StatCardSide | undefined = backSide
    ? {
        color: backSide.color ?? color,
        description: backSide.description ?? description,
        icon: backSide.icon ?? Icon,
        value: backSide.value ?? value,
        title: backSide.title ?? title,
      }
    : undefined;

  const renderContent = (side: StatCardSide) => {
    const { color: sideColor, description: sideDesc, icon: SideIcon, value: sideValue, title: sideTitle } = side;
    return (
      <div className="flex h-full flex-col justify-between">
        <div>
          <span className="block text-lg font-semibold">{sideTitle}</span>
          <span className="text-default-500 block text-xs">{sideDesc}</span>
        </div>
        <div className="mt-8 flex items-end justify-between">
          <SideIcon
            className={clsx("text-4xl transition-transform duration-300 group-hover:scale-110", colors[sideColor].icon)}
            weight="duotone"
          />
          <span className={clsx("text-4xl font-bold", colors[sideColor].value)}>{sideValue}</span>
        </div>
      </div>
    );
  };

  const handleFlip = () => {
    if (hasBackSide) {
      setIsFlipped(!isFlipped);
    }
  };

  return (
    <Card
      isPressable={hasBackSide}
      onPress={handleFlip}
      className={clsx(
        "group relative h-40 cursor-pointer overflow-hidden transition-all duration-300",
        colors[color].gradient,
        hasBackSide ? "hover:opacity-90" : "hover:opacity-90"
      )}
    >
      <CardBody className="relative h-full overflow-hidden p-4">
        {/* Front Side */}
        <div
          className={clsx(
            "absolute inset-0 h-full w-full p-4 transition-all duration-500",
            isFlipped ? "-translate-y-full opacity-0" : "translate-y-0 opacity-100"
          )}
        >
          {renderContent(frontSide)}
        </div>

        {/* Back Side */}
        {hasBackSide && resolvedBackSide && (
          <div
            className={clsx(
              "absolute inset-0 h-full w-full p-4 transition-all duration-500",
              isFlipped ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
            )}
          >
            {renderContent(resolvedBackSide)}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

const colors = {
  amber: {
    icon: "text-amber-500",
    value: "text-amber-600 dark:text-amber-200",
    gradient:
      "bg-gradient-to-br from-amber-100 to-white dark:from-amber-950 dark:via-neutral-900 dark:to-neutral-950 ring-1 ring-amber-100 dark:ring-amber-500/20 group-hover:ring-amber-200 dark:group-hover:ring-amber-500/40",
  },
  teal: {
    icon: "text-teal-500",
    value: "text-teal-600 dark:text-teal-200",
    gradient:
      "bg-gradient-to-br from-teal-100 to-white dark:from-teal-950 dark:via-neutral-900 dark:to-neutral-950 ring-1 ring-teal-100 dark:ring-teal-500/20 group-hover:ring-teal-200 dark:group-hover:ring-teal-500/40",
  },
  purple: {
    icon: "text-purple-500",
    value: "text-purple-600 dark:text-purple-200",
    gradient:
      "bg-gradient-to-br from-purple-100 to-white dark:from-purple-950 dark:via-neutral-900 dark:to-neutral-950 ring-1 ring-purple-100 dark:ring-purple-500/20 group-hover:ring-purple-200 dark:group-hover:ring-purple-500/40",
  },
} as const;
